import React from "react";
import {
    Card,
    CardBody,
    CardHeader,
    FormControl,
    FormLabel,
    Heading,
    Input,
    Select,
    Text,
    VStack,
} from "@chakra-ui/react";
import { getColor } from "../../../brandConfig";
import { ClientFormData } from "../types";

interface PersonalInfoFormProps {
    formData: Pick<ClientFormData, "fName" | "lName" | "email" | "phoneNumber" | "role">;
    emailChanged: boolean;
    phoneChanged: boolean;
    onChange: (field: string, value: string) => void;
}

export const PersonalInfoForm: React.FC<PersonalInfoFormProps> = ({
    formData,
    emailChanged,
    phoneChanged,
    onChange,
}) => {
    const cardGradientBg = getColor("background.cardGradient");
    const cardBorder = getColor("border.darkCard");
    const textSecondary = getColor("text.secondaryDark");
    const textMuted = getColor("text.mutedDark");
    const textInverse = getColor("text.inverse");
    const primaryColor = getColor("primary");

    const inputProps = {
        bg: "rgba(255, 255, 255, 0.05)",
        border: "1px",
        borderColor: cardBorder,
        color: textInverse,
        _placeholder: { color: textMuted },
        _focus: {
            borderColor: primaryColor,
            boxShadow: `0 0 0 1px ${primaryColor}`,
        },
    };

    return (
        <Card
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            borderWidth="1px"
            borderColor={cardBorder}
            overflow="hidden"
        >
            <CardHeader borderBottomWidth="1px" borderColor={cardBorder}>
                <Heading size="md" color={textInverse}>
                    Personal Information
                </Heading>
            </CardHeader>
            <CardBody>
                <VStack spacing={6}>
                    <FormControl>
                        <FormLabel color={textSecondary}>First Name</FormLabel>
                        <Input
                            value={formData.fName}
                            onChange={(e) => onChange("fName", e.target.value)}
                            placeholder="John"
                            {...inputProps}
                        />
                    </FormControl>

                    <FormControl>
                        <FormLabel color={textSecondary}>Last Name</FormLabel>
                        <Input
                            value={formData.lName}
                            onChange={(e) => onChange("lName", e.target.value)}
                            placeholder="Doe"
                            {...inputProps}
                        />
                    </FormControl>

                    <FormControl>
                        <FormLabel color={textSecondary}>
                            Email Address
                            {emailChanged && (
                                <Text as="span" color="orange.500" fontSize="sm" ml={2}>
                                    (Verification required)
                                </Text>
                            )}
                        </FormLabel>
                        <Input
                            type="email"
                            value={formData.email}
                            onChange={(e) => onChange("email", e.target.value)}
                            placeholder="john@example.com"
                            {...inputProps}
                            borderColor={emailChanged ? "orange.300" : cardBorder}
                        />
                        {emailChanged && (
                            <Text fontSize="sm" color="orange.600" mt={1}>
                                Email verification will be required to save this change
                            </Text>
                        )}
                    </FormControl>

                    <FormControl>
                        <FormLabel color={textSecondary}>
                            Phone Number
                            {phoneChanged && (
                                <Text as="span" color="orange.500" fontSize="sm" ml={2}>
                                    (SMS verification required)
                                </Text>
                            )}
                        </FormLabel>
                        <Input
                            type="tel"
                            value={formData.phoneNumber}
                            onChange={(e) => onChange("phoneNumber", e.target.value)}
                            placeholder="+61412345678"
                            {...inputProps}
                            borderColor={phoneChanged ? "orange.300" : cardBorder}
                        />
                        {phoneChanged && (
                            <Text fontSize="sm" color="orange.600" mt={1}>
                                SMS verification will be required to save this change
                            </Text>
                        )}
                    </FormControl>

                    <FormControl>
                        <FormLabel color={textSecondary}>Role</FormLabel>
                        <Select
                            value={formData.role}
                            onChange={(e) => onChange("role", e.target.value)}
                            {...inputProps}
                        >
                            <option value="BUYER">Buyer</option>
                            <option value="SELLER">Seller</option>
                            <option value="BOTH">Both</option>
                        </Select>
                    </FormControl>
                </VStack>
            </CardBody>
        </Card>
    );
};
